import {
    OnChangeFn, RowData, SortingState, TableOptions, VisibilityState, getCoreRowModel, getSortedRowModel, useReactTable,
} from "@tanstack/react-table"
import { useState } from "react"
import { useLocation } from "react-router-dom"

type UseDataTableOptions<T extends RowData> =
    Omit<TableOptions<T>, "getCoreRowModel"> & Partial<Pick<TableOptions<T>, "getCoreRowModel">> & {
        // Initial order; the user can change it by clicking a column title
        initialSorting?: SortingState
        // Columns hidden until the user shows them (e.g. { president: false })
        initialColumnVisibility?: VisibilityState
        // Key for the saved column choices; defaults to the page URL. Give it when a page has
        // more than one table.
        tableId?: string
    }

const STORAGE_PREFIX = "ibexiq.table-columns."

const readVisibility = (key: string): VisibilityState | null => {
    try {
        const raw = window.localStorage.getItem(STORAGE_PREFIX + key)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const saveVisibility = (key: string, value: VisibilityState) => {
    try {
        window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value))
    } catch {
        // Storage blocked (private window): the choice lasts until the page is left
    }
}

// Every table in the app: no pagination, click a column title to sort (asc -> desc -> none).
// Columns sort by their accessor value; give display-only columns an accessorFn to make them
// sortable, and set enableSorting: false on action/control columns.
// Users can show/hide columns (DataTableTemplate renders the picker); the choice is saved in this
// browser per table. Set enableHiding: false on columns that must always show.
const useDataTable = <T extends RowData>({
    initialSorting = [],
    initialColumnVisibility = {},
    tableId,
    state,
    ...options
}: UseDataTableOptions<T>) => {
    const { pathname } = useLocation()
    const storageKey = tableId ?? pathname
    const [sorting, setSorting] = useState<SortingState>(initialSorting)
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        () => ({ ...initialColumnVisibility, ...(readVisibility(storageKey) ?? {}) })
    )

    // Saved only when the user changes it, so other tables on the page never overwrite it
    const handleVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
        setColumnVisibility(prev => {
            const next = typeof updater === "function" ? updater(prev) : updater
            saveVisibility(storageKey, next)
            return next
        })
    }

    return useReactTable<T>({
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: setSorting,
        onColumnVisibilityChange: handleVisibilityChange,
        ...options,
        state: { sorting, columnVisibility, ...state },
        defaultColumn: {
            sortUndefined: "last",
            ...options.defaultColumn,
        },
    })
}

export default useDataTable
